import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, BarChart3, Megaphone, X } from "lucide-react";
import { getConsent, setConsent } from "@/lib/analytics";

/**
 * GDPR / UK PECR-compliant cookie banner.
 *
 * Industry standards implemented:
 *  - Strictly-necessary cookies are exempt and cannot be disabled.
 *  - Non-essential categories (analytics, marketing) default to OFF and require
 *    an active opt-in (no pre-ticked boxes).
 *  - "Accept all", "Reject all" and "Customize" are equally prominent.
 *  - Choice is persisted with a version + timestamp; bumping CONSENT_VERSION re-prompts.
 *  - Users can revisit their choice at any time via the footer "Cookie settings" link
 *    (which dispatches the `gpv:consent-reopen` event this component listens for).
 *  - Closing the banner without a choice does NOT imply consent - it stays closed
 *    only if the user actively chose, otherwise it returns on next visit.
 */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [bannerEl, setBannerEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const existing = getConsent();
    if (existing === null) {
      setVisible(true);
    } else {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }
    const onReopen = () => {
      const cur = getConsent();
      setAnalytics(cur?.analytics ?? false);
      setMarketing(cur?.marketing ?? false);
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener("gpv:consent-reopen", onReopen);
    return () => window.removeEventListener("gpv:consent-reopen", onReopen);
  }, []);

  // Reserve space at the bottom of the page so the fixed banner never overlaps
  // form submit buttons, sticky CTAs, etc.
  useEffect(() => {
    if (!visible || !bannerEl) {
      document.body.style.paddingBottom = "";
      return;
    }
    const apply = () => {
      const h = bannerEl.getBoundingClientRect().height;
      document.body.style.paddingBottom = `${Math.ceil(h)}px`;
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(bannerEl);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
      document.body.style.paddingBottom = "";
    };
  }, [visible, bannerEl, showDetails]);

  if (!visible) return null;

  const acceptAll = () => {
    setConsent({ analytics: true, marketing: true });
    setVisible(false);
  };
  const rejectAll = () => {
    setConsent({ analytics: false, marketing: false });
    setVisible(false);
  };
  const saveCustom = () => {
    setConsent({ analytics, marketing });
    setVisible(false);
  };

  return (
    <>
      {/* Backdrop dims the page when granular settings are open - signals the dialog is modal-ish */}
      {showDetails && (
        <div
          className="fixed inset-0 z-[99] bg-foreground/30 backdrop-blur-sm"
          aria-hidden="true"
        />
      )}
      <div
        ref={setBannerEl}
        role="dialog"
        aria-modal={showDetails ? "true" : "false"}
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-desc"
        className="fixed inset-x-0 bottom-0 z-[100] border-t border-border bg-background shadow-2xl supports-[backdrop-filter]:bg-background/95 supports-[backdrop-filter]:backdrop-blur"
      >
        <div className="container mx-auto max-w-5xl px-4 py-5 sm:py-6">
          {!showDetails ? (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
              <div className="flex shrink-0 items-center justify-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Cookie className="h-5 w-5" aria-hidden="true" />
                </div>
              </div>
              <div className="flex-1">
                <h2 id="cookie-consent-title" className="text-base font-semibold text-foreground">
                  We value your privacy
                </h2>
                <p id="cookie-consent-desc" className="mt-1 text-sm text-muted-foreground">
                  We use strictly necessary cookies to make GetPetVet work. With your
                  permission we'll also use analytics and marketing cookies to
                  understand how the site is used and to show you relevant content.
                  You can change your choice at any time via{" "}
                  <Link to="/cookies" className="underline hover:text-foreground">
                    Cookie settings
                  </Link>
                  . Read our{" "}
                  <Link to="/cookies" className="underline hover:text-foreground">
                    Cookie Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="underline hover:text-foreground">
                    Privacy Policy
                  </Link>
                  .
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" onClick={acceptAll} className="min-w-[120px]">
                    Accept all
                  </Button>
                  <Button size="sm" variant="outline" onClick={rejectAll} className="min-w-[120px]">
                    Reject all
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowDetails(true)}
                    className="min-w-[120px]"
                  >
                    Customize
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowDetails(false)}
                aria-label="Close cookie settings"
                className="absolute right-0 top-0 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
              <h2 id="cookie-consent-title" className="pr-8 text-base font-semibold text-foreground">
                Cookie preferences
              </h2>
              <p id="cookie-consent-desc" className="mt-1 text-sm text-muted-foreground">
                Choose which categories of cookies you're happy for us to use. Your choice is saved on this device.
              </p>

              <ul className="mt-5 space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                <li className="rounded-lg border border-border bg-muted/40 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                      <div>
                        <p className="font-medium text-foreground">Strictly necessary</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Required for the site to work - sign-in, security, saving your preferences. Cannot be disabled.
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 rounded-md bg-primary/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-primary">
                      Always on
                    </span>
                  </div>
                </li>

                <li className="rounded-lg border border-border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <BarChart3 className="mt-0.5 h-5 w-5 shrink-0 text-foreground/70" aria-hidden="true" />
                      <div>
                        <p className="font-medium text-foreground">Analytics</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Anonymous usage data (e.g. Google Analytics) so we can see which features are used and improve them.
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={analytics}
                      onCheckedChange={setAnalytics}
                      aria-label="Allow analytics cookies"
                    />
                  </div>
                </li>

                <li className="rounded-lg border border-border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <Megaphone className="mt-0.5 h-5 w-5 shrink-0 text-foreground/70" aria-hidden="true" />
                      <div>
                        <p className="font-medium text-foreground">Marketing</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          Used to measure ad performance and show you more relevant GetPetVet promotions on other sites.
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={marketing}
                      onCheckedChange={setMarketing}
                      aria-label="Allow marketing cookies"
                    />
                  </div>
                </li>
              </ul>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <Link to="/cookies" className="text-xs text-muted-foreground underline hover:text-foreground">
                  Read our full Cookie Policy
                </Link>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={rejectAll}>
                    Reject all
                  </Button>
                  <Button size="sm" variant="outline" onClick={acceptAll}>
                    Accept all
                  </Button>
                  <Button size="sm" onClick={saveCustom}>
                    Save preferences
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
